import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getMealsRosterPortal, markMealsPortal, bulkMarkMealsPortal,
  getMealsHistoryPortal, getHostelAdmissionsPortal, requestMealsEditPortal,
  getHostelAdmissionOptionsPortal, getAttendanceHistoryPortal, getHostelPaymentsPortal, getInvoicesPortal, getInvoicePortal,
  getBillingAdjustmentsPortal, getHostelRequestsPortal,
} from '../../api';
import { FiCoffee, FiSun, FiMoon, FiUsers, FiClock, FiEdit2, FiLock, FiX, FiDownload, FiUser } from 'react-icons/fi';
import { GROUP_OPTIONS, groupRows } from '../../utils/grouping';
import ResidentProfileModal from '../ResidentProfileModal';

const todayStr = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };

function downloadCsv(filename, rows) {
  const esc = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const csv = rows.map(r => r.map(esc).join(',')).join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function HostelMealsPortal({ hostel }) {
  const [searchParams] = useSearchParams();
  const urlAdmissionId = searchParams.get('admission_id');

  const [date, setDate] = useState(todayStr());
  const [roster, setRoster] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [editHidden, setEditHidden] = useState(false);
  const [editApproval, setEditApproval] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const [admissions, setAdmissions] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyStart, setHistoryStart] = useState(firstOfMonth());
  const [historyEnd, setHistoryEnd] = useState(todayStr());
  const [historyAdmissionId, setHistoryAdmissionId] = useState(urlAdmissionId || '');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [groupBy, setGroupBy] = useState('');
  const [campuses, setCampuses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [profileRecord, setProfileRecord] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);

  const [showRequestForm, setShowRequestForm] = useState(false);
  const [reqAdmissionId, setReqAdmissionId] = useState('');
  const [reqStart, setReqStart] = useState(todayStr());
  const [reqEnd, setReqEnd] = useState(todayStr());
  const [reqBreakfast, setReqBreakfast] = useState(true);
  const [reqLunch, setReqLunch] = useState(true);
  const [reqDinner, setReqDinner] = useState(true);
  const [reqReason, setReqReason] = useState('');
  const [reqSubmitting, setReqSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getMealsRosterPortal({ hostel_id: hostel.id, date });
      setRoster(res.data.roster);
      setEnabled(res.data.meals_enabled);
      setEditHidden(res.data.edit_hidden);
      setEditApproval(res.data.edit_approval);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load roster');
    }
    setLoading(false);
  }, [hostel.id, date]);

  useEffect(() => { load(); }, [load]);

  const loadPending = useCallback(async () => {
    try {
      const res = await getHostelRequestsPortal({ status: 'pending' });
      setPendingRequests(Array.isArray(res.data) ? res.data : []);
    } catch (e) { /* non-fatal */ }
  }, []);

  useEffect(() => { loadPending(); }, [loadPending]);

  // "admissionId:meal" keys whose value actually differs between a pending
  // request and the currently-confirmed roster row - only THAT specific
  // meal shows "Pending approval" and gets disabled; the other two meals
  // for the same resident/day stay fully editable (a pending change to
  // lunch shouldn't block marking breakfast or dinner).
  const pendingMealSet = useMemo(() => {
    const set = new Set();
    for (const r of pendingRequests) {
      if (r.request_type !== 'meal_edit') continue;
      const d = typeof r.requested_data === 'string' ? JSON.parse(r.requested_data) : (r.requested_data || {});
      if (!(d.date_start <= date && date <= d.date_end)) continue;
      const row = roster.find(x => x.admission_id === r.admission_id);
      if (!row) continue;
      ['breakfast', 'lunch', 'dinner'].forEach(meal => {
        if (!!d[meal] !== !!row[meal]) set.add(`${r.admission_id}:${meal}`);
      });
    }
    return set;
  }, [pendingRequests, date, roster]);

  useEffect(() => {
    getHostelAdmissionsPortal({ status: 'active' }).then(res => {
      setAdmissions(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
    getHostelAdmissionOptionsPortal().then(res => {
      setCampuses(res.data.campuses || []);
      setCourses(res.data.courses || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!historyAdmissionId) return;
    const adm = admissions.find(a => String(a.id) === String(historyAdmissionId));
    if (adm?.date_of_admission) setHistoryStart(adm.date_of_admission.slice(0, 10));
  }, [historyAdmissionId, admissions]);

  const admissionsById = useMemo(() => Object.fromEntries(admissions.map(a => [a.id, a])), [admissions]);
  const campusNameById = useMemo(() => Object.fromEntries(campuses.map(c => [c.id, c.name])), [campuses]);
  const courseNameById = useMemo(() => Object.fromEntries(courses.map(c => [c.id, c.name])), [courses]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params = { hostel_id: hostel.id, start: historyStart, end: historyEnd };
      if (historyAdmissionId) params.admission_id = historyAdmissionId;
      const res = await getMealsHistoryPortal(params);
      setHistory(res.data);
    } catch (e) { /* non-fatal */ }
    setHistoryLoading(false);
  }, [hostel.id, historyStart, historyEnd, historyAdmissionId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const toggleMeal = async (r, meal) => {
    // Only un-marking an already-served meal counts as "editing" - marking a
    // meal served for the first time never needs confirmation.
    const isEdit = !!r[meal];
    if (isEdit) {
      const msg = editApproval
        ? `${r.full_name}'s ${meal} for ${date} is already marked served. Un-marking it will need admin approval before it takes effect. Continue?`
        : `${r.full_name}'s ${meal} for ${date} is already marked served. Un-mark it?`;
      if (!window.confirm(msg)) return;
    }
    const next = { breakfast: !!r.breakfast, lunch: !!r.lunch, dinner: !!r.dinner, [meal]: !r[meal] };
    setRoster(prev => prev.map(x => x.admission_id === r.admission_id ? { ...x, ...next } : x));
    setError(''); setSuccess('');
    try {
      const res = await markMealsPortal({ admission_id: r.admission_id, meal_date: date, ...next });
      if (res.data.pending) {
        setSuccess(res.data.message || 'Change submitted for admin approval.');
        load();
        loadPending();
      } else {
        loadHistory();
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save');
      load();
    }
  };

  const markAllServed = async () => {
    setSaving(true);
    try {
      const entries = roster.map(r => ({ admission_id: r.admission_id, breakfast: true, lunch: true, dinner: true }));
      await bulkMarkMealsPortal({ hostel_id: hostel.id, meal_date: date, entries });
      load(); loadHistory();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to bulk mark');
    }
    setSaving(false);
  };

  const openRequestForm = (row) => {
    setReqAdmissionId(row ? row.admission_id : (admissions[0]?.id || ''));
    setReqStart(row ? row.meal_date.slice(0, 10) : todayStr());
    setReqEnd(row ? row.meal_date.slice(0, 10) : todayStr());
    setReqBreakfast(row ? !row.breakfast : true);
    setReqLunch(row ? !row.lunch : true);
    setReqDinner(row ? !row.dinner : true);
    setReqReason('');
    setShowRequestForm(true);
  };

  const submitRequest = async () => {
    if (!reqAdmissionId || !reqStart || !reqEnd) { setError('Please fill all fields'); return; }
    setReqSubmitting(true); setError(''); setSuccess('');
    try {
      const res = await requestMealsEditPortal({
        admission_id: reqAdmissionId, date_start: reqStart, date_end: reqEnd,
        breakfast: reqBreakfast, lunch: reqLunch, dinner: reqDinner, reason: reqReason,
      });
      setSuccess(res.data.message);
      setShowRequestForm(false);
      load(); loadHistory();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to submit');
    }
    setReqSubmitting(false);
  };

  const viewProfile = (admissionId, fallbackName) => {
    setProfileRecord(admissionsById[admissionId] || { id: admissionId, full_name: fallbackName });
  };

  const exportHistoryCsv = () => {
    const rows = [['Date', 'Resident', 'Breakfast', 'Lunch', 'Dinner']];
    history.forEach(h => rows.push([new Date(h.meal_date).toLocaleDateString('en-PK'), h.full_name, h.breakfast ? 'Yes' : 'No', h.lunch ? 'Yes' : 'No', h.dinner ? 'Yes' : 'No']));
    downloadCsv(`meals-${hostel.name}-${historyStart}_to_${historyEnd}.csv`, rows);
  };

  const servedB = roster.filter(r => r.breakfast).length;
  const servedL = roster.filter(r => r.lunch).length;
  const servedD = roster.filter(r => r.dinner).length;
  const rosterGroups = groupRows(roster, groupBy, admissionsById, campusNameById, courseNameById);
  const historyGroups = groupRows(history, groupBy, admissionsById, campusNameById, courseNameById);

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title"><FiCoffee /> Daily Meals</h2>
          <p style={{ margin: '4px 0 0' }}>Track meals served to your residents</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <input type="date" className="form-control" value={date} max={todayStr()} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <select className="form-control" value={groupBy} onChange={e => setGroupBy(e.target.value)}>
              {GROUP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {!enabled && !loading && (
        <div className="alert alert-warning">Meal tracking has not been enabled for your hostel yet. Contact the admin office to enable it.</div>
      )}

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : enabled && (
        <>
          <div className="stats-grid" style={{ marginBottom: '18px' }}>
            <div className="stat-card stat-purple">
              <div className="stat-icon"><FiUsers /></div>
              <div className="stat-content"><div className="stat-value">{roster.length}</div><div className="stat-label">Total Residents</div></div>
            </div>
            <div className="stat-card stat-orange">
              <div className="stat-icon"><FiSun /></div>
              <div className="stat-content"><div className="stat-value">{servedB}</div><div className="stat-label">Breakfast Served</div></div>
            </div>
            <div className="stat-card stat-blue">
              <div className="stat-icon"><FiCoffee /></div>
              <div className="stat-content"><div className="stat-value">{servedL}</div><div className="stat-label">Lunch Served</div></div>
            </div>
            <div className="stat-card stat-green">
              <div className="stat-icon"><FiMoon /></div>
              <div className="stat-content"><div className="stat-value">{servedD}</div><div className="stat-label">Dinner Served</div></div>
            </div>
          </div>

          <div className="action-buttons" style={{ marginBottom: '14px' }}>
            <button className="btn btn-dark btn-small" onClick={markAllServed} disabled={saving || !roster.length}>
              {saving ? 'Saving...' : 'Mark All Served'}
            </button>
            {!editHidden && (
              <button className="btn btn-secondary btn-small" onClick={() => openRequestForm(null)}>
                <FiEdit2 /> {editApproval ? 'Request a Correction' : 'Correct a Past Date'}
              </button>
            )}
          </div>

          {rosterGroups.map((group, gi) => (
            <div key={gi} style={{ marginBottom: '18px' }}>
              {group.label && <h4 style={{ fontSize: '13px', opacity: 0.7, margin: '0 0 8px' }}>{group.label} ({group.rows.length})</h4>}
              <div className="table-container">
                <table className="data-table">
                  <thead><tr><th>#</th><th>Resident</th><th>Breakfast</th><th>Lunch</th><th>Dinner</th></tr></thead>
                  <tbody>
                    {group.rows.length === 0 ? (
                      <tr><td colSpan="5" style={{ textAlign: 'center', padding: '30px' }}>No active residents.</td></tr>
                    ) : group.rows.map((r, i) => (
                      <tr key={r.admission_id}>
                        <td>{i + 1}</td>
                        <td>
                          <strong>{r.full_name}</strong>{' '}
                          <button className="btn btn-small btn-secondary" title="View resident profile" onClick={() => viewProfile(r.admission_id, r.full_name)}><FiUser size={12} /></button>
                        </td>
                        {['breakfast', 'lunch', 'dinner'].map(meal => (
                          <td key={meal}>
                            {pendingMealSet.has(`${r.admission_id}:${meal}`) ? (
                              <span className="badge badge-gold" title="This change is awaiting admin approval">
                                <FiClock size={12} /> Pending
                              </span>
                            ) : (
                              <button className={`btn btn-small ${r[meal] ? 'btn-success' : 'btn-secondary'}`} onClick={() => toggleMeal(r, meal)}>
                                {r[meal] ? 'Served' : 'Not served'}
                              </button>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </>
      )}

      {/* Request Correction form */}
      {showRequestForm && (
        <div className="modal-overlay" onClick={() => setShowRequestForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '460px' }}>
            <div className="modal-header">
              <h3>{editApproval ? 'Request a Correction' : 'Correct a Past Date'}</h3>
              <button className="modal-close" onClick={() => setShowRequestForm(false)}><FiX /></button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gap: '12px' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Resident</label>
                <select className="form-control" value={reqAdmissionId} onChange={e => setReqAdmissionId(e.target.value)}>
                  {admissions.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
                </select>
              </div>
              <div className="form-row" style={{ gap: '10px' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>From</label>
                  <input type="date" className="form-control" value={reqStart} max={todayStr()} onChange={e => setReqStart(e.target.value)} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>To</label>
                  <input type="date" className="form-control" value={reqEnd} max={todayStr()} onChange={e => setReqEnd(e.target.value)} />
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Correct meals to</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {[['breakfast', reqBreakfast, setReqBreakfast], ['lunch', reqLunch, setReqLunch], ['dinner', reqDinner, setReqDinner]].map(([label, val, setter]) => (
                    <label key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', textTransform: 'capitalize' }}>
                      <input type="checkbox" checked={val} onChange={e => setter(e.target.checked)} /> {label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Reason</label>
                <textarea className="form-control" rows="2" value={reqReason} onChange={e => setReqReason(e.target.value)} placeholder="Why is this correction needed?" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowRequestForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitRequest} disabled={reqSubmitting}>{reqSubmitting ? 'Submitting...' : 'Submit'}</button>
            </div>
          </div>
        </div>
      )}

      {/* History */}
      <div style={{ marginTop: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}><FiClock /> Meal History</h3>
          <button className="btn btn-secondary btn-small" onClick={exportHistoryCsv} disabled={!history.length}><FiDownload /> Export CSV</button>
        </div>
        <div className="form-row" style={{ marginBottom: '14px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Resident</label>
            <select className="form-control" value={historyAdmissionId} onChange={e => setHistoryAdmissionId(e.target.value)}>
              <option value="">All residents</option>
              {admissions.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>From{historyAdmissionId ? ' (defaults to enrollment date)' : ''}</label>
            <input type="date" className="form-control" value={historyStart} onChange={e => setHistoryStart(e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>To</label>
            <input type="date" className="form-control" value={historyEnd} max={todayStr()} onChange={e => setHistoryEnd(e.target.value)} />
          </div>
        </div>

        {historyLoading ? (
          <div className="loading"><div className="spinner"></div></div>
        ) : historyGroups.map((group, gi) => (
          <div key={gi} style={{ marginBottom: '18px' }}>
            {group.label && <h4 style={{ fontSize: '13px', opacity: 0.7, margin: '0 0 8px' }}>{group.label} ({group.rows.length})</h4>}
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Date</th><th>Resident</th><th>Breakfast</th><th>Lunch</th><th>Dinner</th>{!editHidden && <th>Action</th>}</tr></thead>
                <tbody>
                  {group.rows.length === 0 ? (
                    <tr><td colSpan={editHidden ? 5 : 6} style={{ textAlign: 'center', padding: '24px' }}>No records in this range.</td></tr>
                  ) : group.rows.map(row => (
                    <tr key={row.id}>
                      <td>{new Date(row.meal_date).toLocaleDateString('en-PK')}</td>
                      <td>
                        {row.full_name}{' '}
                        <button className="btn btn-small btn-secondary" title="View resident profile" onClick={() => viewProfile(row.admission_id, row.full_name)}><FiUser size={12} /></button>
                      </td>
                      <td>{row.breakfast ? <span className="badge badge-success">Served</span> : <span className="badge badge-muted">—</span>}</td>
                      <td>{row.lunch ? <span className="badge badge-success">Served</span> : <span className="badge badge-muted">—</span>}</td>
                      <td>{row.dinner ? <span className="badge badge-success">Served</span> : <span className="badge badge-muted">—</span>}</td>
                      {!editHidden && (
                        <td>
                          <button className="btn btn-secondary btn-small" onClick={() => openRequestForm(row)}>
                            <FiEdit2 /> {editApproval ? 'Request Edit' : 'Edit'}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
        {editHidden && (
          <p style={{ fontSize: '12px', marginTop: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FiLock size={12} /> Editing past meals is disabled for your hostel by the admin office.
          </p>
        )}
      </div>

      {profileRecord && (
        <ResidentProfileModal
          admission={profileRecord}
          campuses={campuses}
          courses={courses}
          onClose={() => setProfileRecord(null)}
          fetchAttendance={(id) => getAttendanceHistoryPortal({ admission_id: id })}
          fetchMeals={(id) => getMealsHistoryPortal({ admission_id: id })}
          fetchPayments={(id) => getHostelPaymentsPortal({ admission_id: id })}
          fetchInvoices={(id) => getInvoicesPortal({ admission_id: id })}
          fetchInvoiceDetail={(id) => getInvoicePortal(id)}
          fetchAdjustments={(id) => getBillingAdjustmentsPortal({ admission_id: id })}
        />
      )}
    </div>
  );
}
